import React, { Component, Suspense, lazy } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { graphql, withApollo } from '@apollo/client/react/hoc';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'redux';

// components
import Routes from './routes';
import Footer from './components/footer';
import Loader from './components/utils/loader';
import Error from './components/error';

// Actions
import * as actions from './actions/index';

// Utils
import {
  setCookie,
  getCookie,
  LOG,
  getOSUser,
  getUserAgent,
  removeCookie,
  isInsideApp,
  setupZM,
  extractInfoFromURl,
  parseQueryString,
  getParameterByName,
  setLocalStorage,
  setupSmartOcto
} from './helpers/utils';

// Graphql
import createSessionMutation from './graphql/createSession.gql';
import getSessionQuery from './graphql/session.gql';
import { loaderDataListener, sendMessageToLoader } from './components/utils/helpers';

import './stylesheets/main.css';

// Lazy loading components
export const Header = lazy(() => import('./components/header'));
export const NossaContainer = lazy(() => import('./components/user/nossa'));

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sessionLoaded: false,
      messageReceived: false,
      metricsDataReceived: null,
      shouldSendGA4Metrics: false,
    };
    this.callbackNetworkError = this.callbackNetworkError.bind(this);
  }
  UNSAFE_componentWillMount() {
    LOG('BrowserRouter', this.props);
    const { offerId } = this.props;

    // Block session query when offer id is zero
    if (offerId === 0) {
      this.props.setLoader(false);
      this.props.setInvalidOffer(true);
      this.setState({ sessionLoaded: true });
      return;
    }

    if (this.props.receivedUUID) {
      this.props.setSession(this.props.receivedUUID);
    }
  }
  componentDidMount() {
    this.props.isAuthenticated();
    // save query string from first access
    LOG('window.location.search', window.location.search);
    this.props.setParams(window.location.search);

    // Save querytring params in the local storage to be used on purchase
    if (window.location.search) {
      setLocalStorage(`apa-${this.props.offerId}-params`, window.location.search);

      // Set if EasyCheckout is running inside app webview
      this.props.setIsInsideApp(isInsideApp(this.props.offerId));
    }

    // Check if the cookie ex-subscriber exists and set the user email in the SessionReducer
    const exSubscriber = getCookie('apa-ex-subscriber');
    if (exSubscriber) this.props.setIsExSubscriber(exSubscriber);

    const exSubscriberOriginOffer = parseInt(getCookie('apa-ex-subscriber-origin-offer'), 10);
    if (exSubscriberOriginOffer) this.props.setOldOfferId(exSubscriberOriginOffer);

    // Set if app is using the new auth system
    this.props.setIsNossa(this.props.isNossa);
    const inOneDay = new Date(new Date().getTime() + (60 * 1440 * 1000));
    setCookie('isNossa', this.props.isNossa, { expires: inOneDay });

    // start GTM Easy Checkout - NEW KEY
    /* eslint-disable */
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});let f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!=='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer',process.env.REACT_APP_GTM_ID_NEW);

    // Start ZM
    setupZM();

    // Start SmartOcto
    setupSmartOcto();

    loaderDataListener((params) => {
      LOG('[ ALLOWED_DOMAINS ]', process.env, process.env.REACT_APP_ALLOWED_DOMAINS);
      const DOMAINS_REGEX = new RegExp(process.env.REACT_APP_ALLOWED_DOMAINS, 'ig');
      window.T = DOMAINS_REGEX;
      LOG('[ ALLOWED_DOMAINS_REGEX ]', DOMAINS_REGEX);
      if (DOMAINS_REGEX.test(params.origin)) {
        LOG('loaderDataListener - before if', params);
        if (params.data.metrics) {
          LOG('loaderDataListener', params.data.metrics);
          window.metrics = params.data.metrics;
          const queryString = window.location.search ? parseQueryString(window.location.search) : [];
          this.setState({ messageReceived: true, metricsDataReceived: { ...window.metrics, ...queryString } });
          this.props.setProperties(params.data.metrics);
        }

        if (params.data.fn === 'appReady') {
          this.setState({ messageReceived: true, metricsDataReceived: { url: '' } });
        }
        // Adapt content if the window height is bellow 580px
        if (params.data.appDimensions && params.data.appDimensions.height) {
          const { width } = params.data.appDimensions;
          const contentPageHeight = parseInt(params.data.appDimensions.height - 100, 10); // 100 = header+footer height
          const css = document.createElement('style');
          css.type = 'text/css';
          css.innerHTML = `.content-page { height: ${contentPageHeight}px !important }`;
          if (width) {
            css.innerHTML += `.App, .bar-back, .header, .footer { width: ${width}px; max-width: none; } .apa-modal { max-width: none !important; }`;
          }
          document.body.appendChild(css);
        }
      }

      this.setState({ shouldSendGA4Metrics: true });
    });

    sendMessageToLoader('appReady');
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if (this.props.receivedUUID) {
      LOG('AppProps', newProps);
      this.props.setOfferId(this.props.offerId);
      if (!this.state.sessionLoaded) {
        this.setState({ sessionLoaded: true });
      }
    }

    if (newProps.errorGQL && this.props.errors.length === 0) {
      LOG('[App][GetCart]', newProps.errorGQL);
      this.props.setError(newProps.errorGQL, this.callbackNetworkError);
      this.props.setLoader(false);
      this.props.setMetricsReady(true);
      this.props.setModality('none');
    }

    if (newProps.sessionCart) {
      if ('offer' in newProps.sessionCart && newProps.sessionCart.offer.modality) {
        this.props.setOfferId(newProps.offerId);
        this.props.setModality(newProps.sessionCart.offer.modality);
        this.props.setPrice(newProps.sessionCart.offer.priceTo);
        this.props.setHidePriceFrom(newProps.sessionCart.offer.hidePriceFrom);
        this.props.setHideAnnualPrice(newProps.sessionCart.offer.showAnnualPrice);
      }
      if (this.state.shouldSendGA4Metrics) {
        this.props.sendGA4Metrics('add_to_cart', newProps.sessionCart.offer);
        this.setState({ shouldSendGA4Metrics: false })
      }
    }
  }

  shouldComponentUpdate() {
    return !this.props.errors.length || !this.props.errorGQL;
  }

  componentDidUpdate(_, prevState) {
    if (prevState.messageReceived !== this.state.messageReceived && prevState.metricsDataReceived !== this.state.metricsDataReceived) {
      const { receivedUUID, errors } = this.props;
      const playerId = getParameterByName('playerID');
      if (!receivedUUID && !errors.length) {
        const dataSerrialized = extractInfoFromURl(this.state.metricsDataReceived.url);
        // get url params to check if it has a offer to invalidate uuid
        const urlParams = new URLSearchParams(window.location.search);
        const oldOfferId = parseInt(urlParams.get('originOffer'), 10);
        const uuidToInvalidate = getCookie(`apa-${oldOfferId}`);
        // removing old uuid cookie to avoid errors
        removeCookie(`apa-${oldOfferId}`);

        LOG('[OLD UUID]', uuidToInvalidate);
        this.props.client.mutate({
          mutation: createSessionMutation,
          fetchPolicy: 'no-cache',
          variables: {
            offerId: this.props.offerId,
            editoria: dataSerrialized.editoria,
            idMateria: dataSerrialized.idMateria,
            subeditoria: dataSerrialized.subeditoria,
            url: dataSerrialized.url,
            utmCampaign: this.state.metricsDataReceived.utm_campaign,
            utmContent: this.state.metricsDataReceived.utm_content,
            utmMedium: this.state.metricsDataReceived.utm_medium,
            utmSource: this.state.metricsDataReceived.utm_source,
            ...(urlParams.has('originOffer') ? { uuid: uuidToInvalidate } : {}),
            ...(playerId && playerId !== '' && playerId !== 'null' && playerId !== 'undefined' ? { playerId } : {})
          }
        }).then((response) => {
          LOG('[Create Session Response]', response.data);
          if (response.data) {
            const modality = response.data.newSession.offer.modality.replace(/ /g, '-').toLowerCase();
            const { priceTo: price, hidePriceFrom, hideAnnualPrice } = response.data.newSession.offer;
            this.props.setModality(modality);
            this.props.setOfferId(this.props.offerId);
            this.props.setPrice(price);
            setCookie(`apa-${this.props.offerId}`, response.data.newSession.uuid);
            this.props.setSession(response.data.newSession.uuid);
            this.props.setInvalidOffer(false);
            this.props.setOldOfferId(oldOfferId);
            this.props.setOldOfferPrice(response.data.newSession.oldfirstMonthPrice);
            this.props.setHidePriceFrom(hidePriceFrom);
            this.props.setHideAnnualPrice(hideAnnualPrice);
            this.props.setLoader(false);
            this.setState({ sessionLoaded: true });
            if (this.state.shouldSendGA4Metrics) this.props.sendGA4Metrics('add_to_cart', response.data.newSession.offer);
          }
        }).catch(({ graphQLErrors, networkError }) => {
          LOG('[Create Session Error]', graphQLErrors, networkError);
          this.setState({ sessionLoaded: true });
          // Invalid offer
          if (graphQLErrors.length) {
            this.props.setLoader(false);
            this.props.setInvalidOffer(true);
          }
          // Server error
          if (networkError) {
            // Force metricsReady to true if a network error occurs
            this.props.setMetricsReady(true);
            this.props.setModality('none');

            // Set error
            this.props.setError(networkError, this.callbackNetworkError);
          }
        });
      }
    }
  }

  callbackNetworkError() {
    this.props.setLoader(true);
    removeCookie(`apa-${this.props.offerId}`);
    setTimeout(() => {
      window.location.href = `/oferta/${this.props.offerId}${this.props.params}`;
    }, 500);
  }
  componentDidCatch(error) {
    LOG('componentDidCatch', error);
  }

  render() {
    const {
      step,
      icons,
      offerId,
      showModalErrors
    } = this.props;

    LOG('[Create session]', this.props.loading, !this.state.sessionLoaded, !this.props.metricsReady);

    if (this.props.loading || !this.state.sessionLoaded || !this.props.metricsReady) {
      return (
        <div className="App">
          <Loader show />
        </div>
      );
    }
    return (
      <BrowserRouter basename={`/oferta/${offerId}`}>
        <div className={`App ${getOSUser()} ${getUserAgent()}`}>
          <Suspense fallback={<Loader show />}>
            <Header />
            <NossaContainer />
          </Suspense>
          <Routes {...this.props} />
          <Footer step={step} icons={icons} />
          <Error show={showModalErrors} errors={this.props.errors} callback={this.props.callbackErrors} />
        </div>
      </BrowserRouter>
    );
  }
}

App.defaultProps = {
  authenticated: false,
  step: 1,
  icons: [],
  path: '',
  offerId: null,
  invalidOffer: false,
  setSession: () => {},
  setModality: () => {},
  setProperties: () => {},
  uuid: '',
  receivedUUID: '',
  modality: '',
};
App.propTypes = {
  authenticated: PropTypes.bool,
  step: PropTypes.number,
  icons: PropTypes.array,
  path: PropTypes.string,
  offerId: PropTypes.number,
  invalidOffer: PropTypes.bool,
  setSession: PropTypes.func,
  setModality: PropTypes.func,
  sendGA4Metrics: PropTypes.func,
  setProperties: PropTypes.func,
  setParams: PropTypes.func,
  uuid: PropTypes.string,
  receivedUUID: PropTypes.string,
  modality: PropTypes.string,
  metricsReady: PropTypes.bool,
  metrics: PropTypes.object,
  setOfferId: PropTypes.func,
  sessionCart: PropTypes.any,
  errors: PropTypes.any,
  errorGQL: PropTypes.any,
  showModalErrors: PropTypes.bool,
  loading: PropTypes.bool,
  callbackErrors: PropTypes.func,
  client: PropTypes.any,
  setInvalidOffer: PropTypes.func,
  setError: PropTypes.func,
  isAuthenticated: PropTypes.func,
  setLoader: PropTypes.func,
  setPrice: PropTypes.func,
  setMetricsReady: PropTypes.func,
  isNossa: PropTypes.bool,
  setIsNossa: PropTypes.func,
  setIsInsideApp: PropTypes.func,
  setIsExSubscriber: PropTypes.func,
  setOldOfferId: PropTypes.func,
  setOldOfferPrice: PropTypes.func,
  params: PropTypes.string,
  setHidePriceFrom: PropTypes.func,
  setHideAnnualPrice: PropTypes.func
};

const withData = graphql(getSessionQuery, {
  skip: props => (!props.uuid && !props.receivedUUID) || props.errorGQL,
  options: props => ({
    variables: {
      uuid: props.receivedUUID || ''
    }
  }),
  props: ({ data: { loading, sessionCart, error } }) => ({
    loading,
    sessionCart,
    errorGQL: error
  })
});

export default compose(withData, connect(state => ({
  loadingUserAuthentication: state.UserReducer.loadingUserAuthentication,
  authenticated: state.UserReducer.authenticated,
  userAction: state.UserReducer.userAction,
  step: state.StepsReducer.step,
  icons: state.StepsReducer.icons,
  path: state.StepsReducer.path,
  barBackOpen: state.StepsReducer.barBackOpen,
  uuid: state.SessionReducer.uuid,
  invalidOffer: state.SessionReducer.invalidOffer,
  errors: state.ErrorsReducer.error,
  showModalErrors: state.ErrorsReducer.showModal,
  callbackErrors: state.ErrorsReducer.callback,
  isSubscriber: state.UserReducer.user.isSubscriber,
  loadingUser: state.UserReducer.loadingUser,
  modality: state.SessionReducer.modality,
  isPriceFromHidden: state.SessionReducer.isPriceFromHidden,
  isAnnualPriceHidden: state.SessionReducer.isAnnualPriceHidden,
  metricsReady: !!(state.MetricsReducer.ready && state.MetricsReducer.receivedProps && (state.SessionReducer.modality || state.SessionReducer.invalidOffer)),
  metrics: state.MetricsReducer.metrics,
  params: state.MetricsReducer.params,
}), {
  ...actions,
}))(withApollo(App));
